import React from 'react';
import { X, User, DollarSign, Calendar, Clock, CreditCard, CheckCircle, AlertCircle, XCircle } from 'lucide-react';

const ContributionDetailsModal = ({ isOpen, onClose, contribution }) => {
  if (!isOpen || !contribution) return null;

  const formatAmount = (amount) => {
    if (!amount) return '0 F CFA';
    
    const numAmount = parseFloat(amount);
    if (numAmount >= 1000000) {
      return `${(numAmount / 1000000).toFixed(1)}M F CFA`;
    } else if (numAmount >= 1000) {
      return `${(numAmount / 1000).toFixed(1)}K F CFA`;
    }
    return `${numAmount.toLocaleString()} F CFA`;
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'paid':
        return {
          label: 'Payé',
          icon: CheckCircle,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          iconColor: 'text-green-600'
        };
      case 'pending':
        return {
          label: 'En attente',
          icon: Clock,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600'
        };
      case 'overdue':
        return {
          label: 'En retard',
          icon: XCircle,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          iconColor: 'text-red-600'
        };
      default:
        return {
          label: status,
          icon: AlertCircle,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          iconColor: 'text-gray-600'
        };
    }
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      'cash': 'Espèces',
      'mobile_money': 'Mobile Money',
      'bank_transfer': 'Virement bancaire',
      'online': 'En ligne',
      'check': 'Chèque'
    };
    return methods[method] || method;
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[monthNumber - 1] || `Mois ${monthNumber}`;
  };

  const statusConfig = getStatusConfig(contribution.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Détails de la Cotisation</h2>
              <p className="text-sm text-gray-500">Contribution #{contribution.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Membre */}
          <div className="mb-6">
            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <User className="h-6 w-6 text-gray-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">
                  {contribution.user?.first_name} {contribution.user?.last_name}
                </h3>
                <p className="text-sm text-gray-600">{contribution.user?.email}</p>
                <div className="mt-1">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                    {contribution.user?.fraternity || 'Non assigné'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Informations principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Montant */}
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <DollarSign className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm text-gray-600">Montant</p>
                  <p className="text-xl font-semibold text-green-700">
                    {formatAmount(contribution.amount)}
                  </p>
                </div>
              </div>
            </div>

            {/* Statut */}
            <div className={`${statusConfig.bgColor} p-4 rounded-lg`}>
              <div className="flex items-center space-x-3">
                <StatusIcon className={`h-5 w-5 ${statusConfig.iconColor}`} />
                <div>
                  <p className="text-sm text-gray-600">Statut</p>
                  <p className={`text-lg font-semibold ${statusConfig.textColor}`}>
                    {statusConfig.label}
                  </p>
                </div>
              </div>
            </div>

            {/* Période */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <Calendar className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm text-gray-600">Période</p>
                  <p className="text-lg font-semibold text-blue-700">
                    {getMonthName(contribution.period_month)} {contribution.period_year}
                  </p>
                </div>
              </div>
            </div>

            {/* Méthode de paiement */}
            <div className="bg-orange-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3">
                <CreditCard className="h-5 w-5 text-orange-600" />
                <div>
                  <p className="text-sm text-gray-600">Méthode de paiement</p>
                  <p className="text-lg font-semibold text-orange-700">
                    {getPaymentMethodLabel(contribution.payment_method)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Informations temporelles */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Informations temporelles</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Date de création</p>
                <p className="font-medium text-gray-900">
                  {new Date(contribution.created_at).toLocaleString('fr-FR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Dernière modification</p>
                <p className="font-medium text-gray-900">
                  {new Date(contribution.updated_at).toLocaleString('fr-FR', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Référence */}
          {contribution.reference && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Référence</h4>
              <p className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded font-mono">
                {contribution.reference}
              </p>
            </div>
          )}

          {/* Notes */}
          {contribution.notes && (
            <div className="border-t border-gray-200 pt-6 mt-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Notes</h4>
              <p className="text-sm text-gray-700 bg-gray-50 px-3 py-2 rounded">
                {contribution.notes}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContributionDetailsModal;